import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Users, UserCheck, Sparkles, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import api, { apiError } from "../../../lib/api";
import { fmtDate } from "../../../lib/time";
import { Label, StatCard } from "../../../components/common";

const PERIODS = [["day", "Today"], ["wtd", "Week to date"], ["mtd", "Month to date"]];

export default function PlatformSystemReport() {
  const [period, setPeriod] = useState("day");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get(`/platform/system-report?period=${period}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [period]);

  if (!d) return <div className="text-slate-400">Loading system report…</div>;
  const g = d.global;

  return (
    <div className="space-y-6" data-testid="system-report">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Label>Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">System Report</h1></div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="period-toggle">
          {PERIODS.map(([k, lbl]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${period === k ? "bg-[#4338CA] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Orgs onboarded" value={g.orgs_onboarded} icon={Building2} accent="#4338CA" testId="sr-onboarded" />
        <StatCard label="Orgs active" value={g.orgs_active} icon={UserCheck} accent="#10B981" testId="sr-active" />
        <StatCard label="New orgs" value={g.new_orgs} icon={Sparkles} accent="#F59E0B" testId="sr-new" />
        <StatCard label="Employees (active orgs)" value={g.employees_total_active_orgs} icon={Users} accent="#0EA5E9" testId="sr-emp-total" />
        <StatCard label="Employees active" value={g.employees_active} icon={Users} accent="#4338CA" testId="sr-emp-active" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sr-org-table">
        <Label>Summary by organisation & location</Label>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
              <tr>
                <th className="py-2">Organisation</th>
                <th className="text-center">Emp active</th>
                <th className="text-center">Ratings</th>
                <th className="text-center">Score</th>
                <th className="text-center">Scan / NFC</th>
                <th className="text-center">Trend (5d)</th>
                <th className="text-center">To escalate</th>
              </tr>
            </thead>
            <tbody>
              {d.orgs.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-400">No active organisations.</td></tr>}
              {d.orgs.map((o) => (
                <React.Fragment key={o.org_id}>
                  <tr className="border-t border-slate-100" data-testid={`sr-org-${o.org_id}`}>
                    <td className="py-2">
                      <button onClick={() => setOpen((s) => ({ ...s, [o.org_id]: !s[o.org_id] }))} className="flex items-center gap-1 font-semibold text-slate-800" data-testid={`sr-expand-${o.org_id}`}>
                        {o.locations.length > 0 ? (open[o.org_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}
                        {o.org_name}
                      </button>
                    </td>
                    <Cells row={o} />
                  </tr>
                  {open[o.org_id] && o.locations.map((l, i) => (
                    <tr key={`${l.location_name}-${i}`} className="border-t border-slate-50 bg-slate-50/50" data-testid={`sr-loc-${o.org_id}-${i}`}>
                      <td className="py-1.5 pl-9 text-slate-600">{l.location_name}</td>
                      <Cells row={l} />
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-400">Emailed automatically to master admins at 12:00 and 18:00 SAST. Trend always reflects the last 5 days.</p>
    </div>
  );
}

function Cells({ row }) {
  return (
    <>
      <td className="text-center">{row.employees_active}</td>
      <td className="text-center font-semibold">{row.ratings}</td>
      <td className="text-center">{row.score ? row.score.toFixed(2) : "—"}</td>
      <td className="text-center text-slate-500">{row.scan} / {row.nfc}</td>
      <td className="text-center"><Spark trend={row.trend} /></td>
      <td className="text-center">{row.escalations > 0 ? <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 font-bold text-red-600"><AlertTriangle className="h-3 w-3" />{row.escalations}</span> : <span className="text-slate-300">0</span>}</td>
    </>
  );
}

function Spark({ trend }) {
  const max = Math.max(1, ...trend.map((t) => t.count));
  return (
    <div className="inline-flex items-end gap-0.5" title={trend.map((t) => `${fmtDate(t.date)}: ${t.count} @ ${t.score}`).join("\n")}>
      {trend.map((t) => (
        <div key={t.date} className="w-1.5 rounded-sm bg-indigo-400" style={{ height: `${4 + (t.count / max) * 20}px`, opacity: t.count ? 1 : 0.25 }} />
      ))}
    </div>
  );
}
